import { inject, observer } from 'mobx-react';
import { Lambda, observe } from 'mobx';
import * as React from 'react';

import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import { RateCalendarModel } from 'src/common/models/common/rate-calendar-model';

import { DayName, UitkNewDatePicker, UitkNewDatePickerProps } from 'uitk-react-new-date-picker';

export interface NewDateFieldProps {
  analytics?: AnalyticsStore;
  context?: Context;
  dateFieldProps: object;
  fetchDatePickerRates?: () => Promise<RateCalendarModel>;
  isDateless?: boolean;
  midnightBookingOffsetHours?: number;
  numDaysBookableInAdvance?: number;
  onChange: any;
  registerFocusMethod?: (fieldName: string, focusMethod: () => void) => void;
  rfrr: object;
  routeStore?: RouteStore;
  currentHotel?: CurrentHotelStore;
}

export type LocalizedNewDateFieldProps = LocalizedComponentProps & UitkNewDatePickerProps & NewDateFieldProps;

export interface NewDateFieldState {
  mounted: boolean;
  datePickerContent?: RateCalendarModel['datePickerContent'];
  footerText?: string;
  showDatePicker: boolean;
}

@withLocalization
@inject('routeStore', 'analytics', 'context', 'currentHotel')
@observer
export class NewDateField extends React.Component<LocalizedNewDateFieldProps, NewDateFieldState> {
  public state: Readonly<NewDateFieldState> = {
    mounted: false,
    datePickerContent: {},
    footerText: undefined,
    showDatePicker: false
  };

  constructor(props) {
    super(props);

    this.focusButton = React.createRef();

    this.focusDateFieldInput = this.focusDateFieldInput.bind(this);
    this.onMount = this.onMount.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.open = this.open.bind(this);
    this.save = this.save.bind(this);
  }

  public focusButton = null;
  public startRef;
  public endRef;
  private datePickerAnalytics = {};
  private disposerOpenDatePicker: Lambda;

  public static defaultProps = {
    endDate: '',
    startDate: '',
    l10n: null
  };

  public componentDidMount() {
    const { registerFocusMethod, currentHotel } = this.props;
    registerFocusMethod('endDate', this.focusDateFieldInput);
    this.refreshRates();
    this.setState({ mounted: true });
    this.disposerOpenDatePicker = observe(currentHotel, ({ newValue, name }) => {
      if (name === 'openNewDatePicker' && newValue) {
        this.startRef?.current?.click();
        currentHotel.setOpenNewDatePicker(false);
      }
    });
  }

  public componentWillUnmount() {
    if (this.disposerOpenDatePicker) {
      this.disposerOpenDatePicker();
    }
  }

  public open(dateType, isMobile) {
    const { routeStore } = this.props;

    if (isMobile) {
      routeStore.updateSearch({ pwaDialog: `datepicker-${dateType}` });
    }

    this.refreshRates();
    this.setState({ showDatePicker: true });
  }

  private refreshRates(): void {
    const { fetchDatePickerRates } = this.props;

    if (fetchDatePickerRates && typeof fetchDatePickerRates === 'function') {
      fetchDatePickerRates().then((rateCalendar: RateCalendarModel) => {
        if (rateCalendar && typeof rateCalendar === 'object') {
          this.setState({
            datePickerContent: rateCalendar.datePickerContent,
            footerText: rateCalendar.disclaimer
          });
        }
      });
    }
  }

  public save(start, end, isMobile) {
    const { endDate, l10n, onChange, startDate } = this.props;
    const { formatDate } = l10n;
    const formattedEnd = formatDate(end, { raw: 'yyyy-MM-dd' });
    const formattedStart = formatDate(start, { raw: 'yyyy-MM-dd' });

    // prevents submitting the datepicker when no new dates are selected
    if (endDate !== formattedEnd || startDate !== formattedStart) {
      onChange({
        target: {
          name: 'startDate',
          value: formattedStart
        }
      });

      onChange({
        target: {
          name: 'endDate',
          value: formattedEnd
        }
      });
    }

    this.dismiss(isMobile, false);
  }

  public focusDateFieldInput(): void {
    const { dateFieldProps } = this.props;
    const { endInvalid, startInvalid } = dateFieldProps;

    if (startInvalid) {
      this.focusButton = this.startRef && this.startRef.current;
    }

    if (endInvalid) {
      this.focusButton = this.endRef && this.endRef.current;
    }

    // manage focus back to the trigger
    if (this.focusButton && typeof this.focusButton.focus === 'function') {
      this.focusButton.focus();
    }
  }

  public dismiss(isMobile, noUpdate) {
    if (isMobile) {
      this.props.routeStore.goBack();
    }

    if (!noUpdate) {
      this.setState({ showDatePicker: false });
    }
  }

  public onMount(start, end) {
    this.startRef = start;
    this.endRef = end;
  }

  public render() {
    const {
      analytics,
      buttonText,
      context,
      dateFieldProps,
      endDate,
      isDateless,
      midnightBookingOffsetHours,
      numDaysBookableInAdvance,
      startDate,
      l10n
    } = this.props;

    const { mounted, showDatePicker } = this.state;
    const firstDayOfWeek = parseInt(DayName[l10n.getWeekData().firstDay.toUpperCase()], 10);

    this.datePickerAnalytics = {
      closeAnalytics: analytics.setupTracker(':ChangeDatesClose', 'Close Date Picker', analytics.omnitureData),
      doneAnalytics: isDateless
        ? analytics.setupTracker(':DatepickerSearch', 'Search Date Picker', analytics.omnitureData)
        : analytics.setupTracker(':ChangeDatesDone', 'Apply Date Picker', analytics.omnitureData),
      nextPageAnalytics: analytics.setupTracker(':NextMonthClick', 'Next Month Click', analytics.omnitureData),
      previousPageAnalytics: analytics.setupTracker(':PreviousMonthClick', 'Previous Month Click', analytics.omnitureData)
    };

    dateFieldProps['showDatePicker'] = showDatePicker;
    dateFieldProps['endAnalytics'] = analytics.setupTracker(':ChangeDatesCheckOut', 'Open Date Picker');
    dateFieldProps['startAnalytics'] = analytics.setupTracker(':ChangeDatesCheckIn', 'Open Date Picker');

    if (!mounted) {
      dateFieldProps['endPlaceholder'] = context.localDateFormat;
      dateFieldProps['startPlaceholder'] = context.localDateFormat;
    }

    const dialogWrapper = (children, dateType) => (
      <QueryRoute query={{ pwaDialog: `datepicker-${dateType}` }}>
        <Layer id={`home-datepicker-${dateType}`} onDismiss={this.dismiss}>
          {children}
        </Layer>
      </QueryRoute>
    );

    const datePickerProps = {
      analytics: this.datePickerAnalytics,
      buttonText,
      dateFieldProps,
      datePickerContent: this.state.datePickerContent,
      daysBookableInAdvance: numDaysBookableInAdvance,
      dialogWrapper,
      doubleMonths: true,
      endDate,
      footerText: this.state.footerText,
      midnightBookingOffset: midnightBookingOffsetHours,
      onDismissCB: this.dismiss,
      onMountCB: this.onMount,
      onOpenCB: this.open,
      onSubmitCB: this.save,
      startDate,
      firstDayOfWeek
    };

    return <UitkNewDatePicker {...datePickerProps} />;
  }
}
